import axios from 'axios'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',  // Use environment variable or fallback to relative URL
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
  validateStatus: function (status) {
    return status < 500; // Accept any status less than 500 as success
  }
})

// Request interceptor for auth tokens if needed
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('authToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Log successful responses for debugging
    console.log('✅ API Success:', response.config.url, response.status);
    return response;
  },
  (error) => {
    console.error('❌ API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('authToken')
    }
    
    if (error.response?.status === 429) {
      // Rate limit exceeded
      const retryAfter = error.response.headers['retry-after'];
      console.warn(`Rate limit exceeded. Retry after ${retryAfter} seconds`);
    }
    
    // Handle different error types
    if (error.code === 'ECONNABORTED') {
      return Promise.reject(new Error('Request timeout - please try again'));
    }
    
    if (error.code === 'NETWORK_ERROR' || !error.response) {
      return Promise.reject(new Error('Network error - check your connection and backend URL'));
    }
    
    const errorMessage = error.response?.data?.detail || 
                        error.response?.data?.message || 
                        error.message || 
                        'An unexpected error occurred';
    
    return Promise.reject(new Error(errorMessage));
  }
)

// Chat API endpoints
export const chatApi = {
  sendMessage: async (message, sessionId, context = {}, intent = null, clarification_response = null) => {
    const payload = {
      message,
      session_id: sessionId,
      intent,
      context,
    };
    if (clarification_response) {
      payload.clarification_response = clarification_response;
    }
    const response = await api.post('/agents/grc-agent', payload);
    return response.data;
  },

  getChatHistory: async (sessionId) => {
    const response = await api.get(`/ai/chat/${sessionId}`)
    return response.data
  },

  createSession: async () => {
    const response = await api.post('/ai/chat-sessions')
    return response.data
  },

  detectIntent: async (message, sessionId, contextState = {}, user_id = null) => {
    // Ensure session_id is a string
    const session_id = typeof sessionId === 'string' ? sessionId : String(sessionId?.id || sessionId || '');
    // Ensure context is an object or null
    let context = contextState;
    if (typeof contextState === 'string') {
      try {
        context = JSON.parse(contextState);
      } catch {
        context = {};
      }
    }
    if (context === undefined) context = null;
    const payload = {
      message,
      session_id,
      user_id,
      context
    };
    console.log('Intent detection payload:', payload);
    const response = await api.post('/ai/intent-detect', payload);
    return response.data;
  },

  logIntent: async (log) => {
    const response = await api.post('/ai/intent-logs', log)
    return response.data
  },

  getIntentAnalytics: async (params) => {
    const response = await api.get('/ai/intent-analytics', { params })
    return response.data
  },
}

// Issue Management API endpoints
export const issueApi = {
    // Process a message for issue creation/management
    processMessage: async (message, sessionId, clarificationResponse = null, context = null) => {
        try {
            const response = await api.post('/api/issue/process', {
                message,
                session_id: sessionId,
                clarification_response: clarificationResponse,
                context
            });
            return response.data;
        } catch (error) {
            console.error('Error processing issue message:', error);
            throw error;
        }
    },

    // Create an issue from a draft
    createFromDraft: async (draftId, sessionId, editedDraft = null) => {
        try {
            const response = await api.post('/api/issue/create-from-draft', {
                draft_id: draftId,
                session_id: sessionId,
                edited_draft: editedDraft
            });
            return response.data;
        } catch (error) {
            console.error('Error creating issue from draft:', error);
            throw error;
        }
    },

    // Legacy endpoint - to be removed after migration
    submitIssue: async (issueData) => {
        try {
            const response = await api.post('/api/issue/submit', issueData);
            return response.data;
        } catch (error) {
            console.error('Error submitting issue:', error);
            throw error;
        }
    }
};

// Issues API endpoints
export const issuesApi = {
  getIssues: async (params = {}) => {
    const response = await api.get('/api/issues', { params })
    return response.data
  },

  getIssue: async (id) => {
    const response = await api.get(`/api/issues/${id}`)
    return response.data
  },

  createIssue: async (issueData) => {
    const response = await api.post('/api/issues', issueData)
    return response.data
  },

  updateIssue: async (id, issueData) => {
    const response = await api.put(`/api/issues/${id}`, issueData)
    return response.data
  },

  deleteIssue: async (id) => {
    const response = await api.delete(`/api/issues/${id}`)
    return response.data
  }
}

// Controls API endpoints
export const controlsApi = {
  getControls: async (params = {}) => {
    const response = await api.get('/api/controls', { params })
    return response.data
  },

  getControl: async (id) => {
    const response = await api.get(`/api/controls/${id}`)
    return response.data
  },

  createControl: async (controlData) => {
    const response = await api.post('/api/controls', controlData)
    return response.data
  },

  updateControl: async (id, controlData) => {
    const response = await api.put(`/api/controls/${id}`, controlData)
    return response.data
  },

  deleteControl: async (id) => {
    const response = await api.delete(`/api/controls/${id}`)
    return response.data
  }
}

// Risks API endpoints
export const risksApi = {
  getRisks: async (params = {}) => {
    const response = await api.get('/api/risks', { params })
    return response.data
  },

  getRisk: async (id) => {
    const response = await api.get(`/api/risks/${id}`)
    return response.data
  },

  createRisk: async (riskData) => {
    const response = await api.post('/api/risks', riskData)
    return response.data
  },

  updateRisk: async (id, riskData) => {
    const response = await api.put(`/api/risks/${id}`, riskData)
    return response.data
  },

  deleteRisk: async (id) => {
    const response = await api.delete(`/api/risks/${id}`)
    return response.data
  }
}

// Products API endpoints
export const productsApi = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/products', { params })
    return response.data
  },
  
  create: async (productData) => {
    const response = await api.post('/api/products', productData)
    return response.data
  },
  
  update: async (id, productData) => {
    const response = await api.put(`/api/products/${id}`, productData)
    return response.data
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/products/${id}`)
    return response.data
  },
  
  // Legacy methods for backward compatibility
  getProducts: async (params = {}) => {
    const response = await api.get('/api/products', { params })
    return response.data
  },

  getProduct: async (id) => {
    const response = await api.get(`/api/products/${id}`)
    return response.data
  },

  createProduct: async (productData) => {
    const response = await api.post('/api/products', productData)
    return response.data
  },

  updateProduct: async (id, productData) => {
    const response = await api.put(`/api/products/${id}`, productData)
    return response.data
  },

  deleteProduct: async (id) => {
    const response = await api.delete(`/api/products/${id}`)
    return response.data
  }
}

// Entities API endpoints
export const entitiesApi = {
  // Teams
  getTeams: async (params = {}) => {
    const response = await api.get('/api/teams', { params })
    return response.data
  },

  createTeam: async (teamData) => {
    const response = await api.post('/api/teams', teamData)
    return response.data
  },

  updateTeam: async (id, teamData) => {
    const response = await api.put(`/api/teams/${id}`, teamData)
    return response.data
  },

  deleteTeam: async (id) => {
    const response = await api.delete(`/api/teams/${id}`)
    return response.data
  },

  // Products (duplicate for compatibility)
  getProducts: async (params = {}) => {
    const response = await api.get('/api/products', { params })
    return response.data
  },

  createProduct: async (productData) => {
    const response = await api.post('/api/products', productData)
    return response.data
  },

  updateProduct: async (id, productData) => {
    const response = await api.put(`/api/products/${id}`, productData)
    return response.data
  },

  deleteProduct: async (id) => {
    const response = await api.delete(`/api/products/${id}`)
    return response.data
  },

  // Licensed Entities
  getLicensedEntities: async (params = {}) => {
    const response = await api.get('/api/licensed-entities', { params })
    return response.data
  },

  createLicensedEntity: async (entityData) => {
    const response = await api.post('/api/licensed-entities', entityData)
    return response.data
  },

  updateLicensedEntity: async (id, entityData) => {
    const response = await api.put(`/api/licensed-entities/${id}`, entityData)
    return response.data
  },

  deleteLicensedEntity: async (id) => {
    const response = await api.delete(`/api/licensed-entities/${id}`)
    return response.data
  }
}

// Actions API endpoints
export const actionsApi = {
  getActions: async (params = {}) => {
    const response = await api.get('/api/actions', { params })
    return response.data
  },

  getAction: async (id) => {
    const response = await api.get(`/api/actions/${id}`)
    return response.data
  },

  createAction: async (actionData) => {
    const response = await api.post('/api/actions', actionData)
    return response.data
  },

  updateAction: async (id, actionData) => {
    const response = await api.put(`/api/actions/${id}`, actionData)
    return response.data
  },

  deleteAction: async (id) => {
    const response = await api.delete(`/api/actions/${id}`)
    return response.data
  },

  updateActionStatus: async (id, status) => {
    const response = await api.put(`/api/actions/${id}/status`, { status })
    return response.data
  },

  getActionsByIssue: async (issueId) => {
    const response = await api.get(`/api/actions/issue/${issueId}`)
    return response.data
  }
}

// Jobs API endpoints
export const jobsApi = {
  getJobs: async (params = {}) => {
    // ✅ FIXED: Ensure linked_entity_id is a number for proper filtering
    if (params.linked_entity_id) {
      params.linked_entity_id = parseInt(params.linked_entity_id);
    }
    console.log('🔍 Jobs API call with params:', params);
    const response = await api.get('/api/jobs', { params })
    console.log('✅ Jobs API response:', response.data);
    return response.data
  },

  getJob: async (id) => {
    const response = await api.get(`/api/jobs/${id}`)
    return response.data
  },

  createJob: async (jobData) => {
    const response = await api.post('/api/jobs', jobData)
    return response.data
  },

  updateJob: async (id, jobData) => {
    const response = await api.patch(`/api/jobs/${id}`, jobData)
    return response.data
  },

  deleteJob: async (id) => {
    const response = await api.delete(`/api/jobs/${id}`)
    return response.data
  },

  generateJobsFromActionPlan: async (actionPlanId, outline) => {
    const response = await api.post(`/api/jobs/action-plans/${actionPlanId}/generate-jobs`, { outline });
    return response.data;
  },
}

// Taxonomy API endpoints
export const taxonomyApi = {
  getTaxonomy: async (params = {}) => {
    const response = await api.get('/api/taxonomy', { params })
    return response.data
  },

  getTaxonomyHierarchy: async () => {
    const response = await api.get('/api/taxonomy/hierarchy')
    return response.data
  },

  getTaxonomyItem: async (id) => {
    const response = await api.get(`/api/taxonomy/${id}`)
    return response.data
  },

  createTaxonomy: async (taxonomyData) => {
    const response = await api.post('/api/taxonomy', taxonomyData)
    return response.data
  },

  updateTaxonomy: async (id, taxonomyData) => {
    const response = await api.put(`/api/taxonomy/${id}`, taxonomyData)
    return response.data
  },

  deleteTaxonomy: async (id) => {
    const response = await api.delete(`/api/taxonomy/${id}`)
    return response.data
  },

  // Bulk operations
  previewUpload: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/api/taxonomy/preview_upload_xlsx', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  uploadBulk: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/api/taxonomy/upload_xlsx', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  exportTaxonomy: () => {
    return `${api.defaults.baseURL}/api/taxonomy/export_xlsx`
  },

  // Semantic tags for taxonomy
  getSemanticTags: async (taxonomyId) => {
    const response = await api.get(`/api/semantic-tags/taxonomy/${taxonomyId}/tags`)
    return response.data
  },

  regenerateSemanticTags: async (taxonomyId) => {
    const response = await api.post('/api/semantic-tags/regenerate', {
      entity_type: 'taxonomy',
      entity_id: parseInt(taxonomyId)
    })
    return response.data
  },

  // Taxonomy-specific risks
  getTaxonomyRisks: async (taxonomyId) => {
    const response = await api.get(`/api/taxonomy/${taxonomyId}/risks`)
    return response.data
  }
}

// Risk semantic tags API
export const riskSemanticTagsApi = {
  getSemanticTags: async (riskId) => {
    const response = await api.get(`/api/semantic-tags/risk/${riskId}/tags`)
    return response.data
  },

  regenerateSemanticTags: async (riskId) => {
    const response = await api.post('/api/semantic-tags/regenerate', {
      entity_type: 'risk',
      entity_id: parseInt(riskId)
    })
    return response.data
  }
}

// Users API endpoints
export const usersApi = {
  getUsers: async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString()
    const response = await api.get(`/api/users/?${queryParams}`)
    return response.data
  },

  getUser: async (id) => {
    const response = await api.get(`/api/users/${id}`)
    return response.data
  },

  createUser: async (userData) => {
    const response = await api.post('/api/users', userData)
    return response.data
  },

  updateUser: async (id, userData) => {
    const response = await api.put(`/api/users/${id}`, userData)
    return response.data
  },

  deleteUser: async (id) => {
    const response = await api.delete(`/api/users/${id}`)
    return response.data
  },

  getUserTeams: async () => {
    const response = await api.get('/api/users/teams')
    return response.data
  }
}

// Incidents API endpoints
export const incidentsApi = {
  getIncidents: async (params = {}) => {
    const response = await api.get('/api/incidents', { params })
    return response.data
  },

  getIncident: async (id) => {
    const response = await api.get(`/api/incidents/${id}`)
    return response.data
  },

  createIncident: async (incidentData) => {
    const response = await api.post('/api/incidents', incidentData)
    return response.data
  },

  updateIncident: async (id, incidentData) => {
    const response = await api.put(`/api/incidents/${id}`, incidentData)
    return response.data
  },

  deleteIncident: async (id) => {
    const response = await api.delete(`/api/incidents/${id}`)
    return response.data
  },

  getAnalytics: async () => {
    const response = await api.get('/api/incidents/analytics/dashboard')
    return response.data
  }
}

// Dashboard API
export const dashboardApi = {
  getSummary: async () => {
    const response = await api.get('/api/dashboard/summary')
    return response.data
  }
}

// KRI API endpoints
export const kriApi = {
  getKRIs: async (params = {}) => {
    const response = await api.get('/api/kris', { params })
    return response.data
  },
  getKRI: async (id) => {
    const response = await api.get(`/api/kris/${id}`)
    return response.data
  },
  getSummary: async () => {
    const response = await api.get('/api/kris/summary')
    return response.data
  }
}

// Issue semantic tags API
export const issueSemanticTagsApi = {
  getSemanticTags: async (issueId) => {
    const response = await api.get(`/api/semantic-tags/issue/${issueId}/tags`)
    return response.data
  },

  regenerateSemanticTags: async (issueId) => {
    const response = await api.post('/api/semantic-tags/regenerate', {
      entity_type: 'issue',
      entity_id: parseInt(issueId)
    })
    return response.data
  }
}

// Admin APIs
export const adminApi = {
  // Issue Fields
  getIssueFields: async () => {
    const response = await api.get('/api/admin/issue-fields')
    return response.data
  },

  updateIssueField: async (fieldId, updates) => {
    const response = await api.patch(`/api/admin/issue-fields/${fieldId}`, updates)
    return response.data
  },

  getIssueFieldOptions: async (fieldId) => {
    const response = await api.get(`/api/admin/issue-fields/${fieldId}/options`)
    return response.data
  },

  createIssueFieldOption: async (fieldId, optionData) => {
    const response = await api.post(`/api/admin/issue-fields/${fieldId}/options`, optionData)
    return response.data
  },

  updateIssueFieldOption: async (fieldId, optionId, updates) => {
    const response = await api.patch(`/api/admin/issue-fields/${fieldId}/options/${optionId}`, updates)
    return response.data
  },

  deleteIssueFieldOption: async (fieldId, optionId) => {
    const response = await api.delete(`/api/admin/issue-fields/${fieldId}/options/${optionId}`)
    return response.data
  },

  // Risk Fields
  getRiskFields: async () => {
    const response = await api.get('/api/admin/risk-fields')
    return response.data
  },

  updateRiskField: async (fieldName, updates) => {
    const response = await api.patch(`/api/admin/risk-fields/${fieldName}`, updates)
    return response.data
  },

  deleteRiskField: async (fieldName) => {
    const response = await api.delete(`/api/admin/risk-fields/${fieldName}`)
    return response.data
  },

  createRiskField: async (fieldData) => {
    const response = await api.post('/api/admin/risk-fields', fieldData)
    return response.data
  },

  // Taxonomy Fields
  getTaxonomyFields: async () => {
    const response = await api.get('/api/admin/taxonomy-fields')
    return response.data
  },

  updateTaxonomyField: async (fieldName, updates) => {
    const response = await api.patch(`/api/admin/taxonomy-fields/${fieldName}`, updates)
    return response.data
  },

  deleteTaxonomyField: async (fieldName) => {
    const response = await api.delete(`/api/admin/taxonomy-fields/${fieldName}`)
    return response.data
  },

  createTaxonomyField: async (fieldData) => {
    const response = await api.post('/api/admin/taxonomy-fields', fieldData)
    return response.data
  },

  // Action Fields  
  getActionFields: async () => {
    const response = await api.get('/api/admin/action-fields')
    return response.data
  },

  updateActionField: async (fieldName, updates) => {
    const response = await api.patch(`/api/admin/action-fields/${fieldName}`, updates)
    return response.data
  },

  deleteActionField: async (fieldName) => {
    const response = await api.delete(`/api/admin/action-fields/${fieldName}`)
    return response.data
  },

  createActionField: async (fieldData) => {
    const response = await api.post('/api/admin/action-fields', fieldData)
    return response.data
  },

  // Control Fields
  getControlFields: async () => {
    const response = await api.get('/api/admin/control-fields')
    return response.data
  },

  updateControlField: async (fieldName, updates) => {
    const response = await api.patch(`/api/admin/control-fields/${fieldName}`, updates)
    return response.data
  },

  deleteControlField: async (fieldName) => {
    const response = await api.delete(`/api/admin/control-fields/${fieldName}`)
    return response.data
  },

  createControlField: async (fieldData) => {
    const response = await api.post('/api/admin/control-fields', fieldData)
    return response.data
  }
}

// Frameworks API endpoints
export const frameworksApi = {
  getFrameworks: async (params = {}) => {
    const response = await api.get('/api/frameworks', { params })
    return response.data
  },

  getOrganizationFrameworks: async (orgId = 1) => {
    const response = await api.get(`/api/frameworks/organization/${orgId}`)
    return response.data
  },

  selectFramework: async (orgId, frameworkId) => {
    const response = await api.post(`/api/frameworks/organization/${orgId}/select/${frameworkId}`)
    return response.data
  },

  getImportedFrameworks: async () => {
    const response = await api.get('/api/regulatory-frameworks/imported')
    return response.data
  }
}

// Policies API endpoints
export const policiesApi = {
  getPolicies: async (params = {}) => {
    const response = await api.get('/api/policies', { params })
    return response.data
  },

  getPolicy: async (id) => {
    const response = await api.get(`/api/policies/${id}`)
    return response.data
  },

  createPolicy: async (policyData) => {
    const response = await api.post('/api/policies', policyData)
    return response.data
  },

  updatePolicy: async (id, policyData) => {
    const response = await api.put(`/api/policies/${id}`, policyData)
    return response.data
  },

  deletePolicy: async (id) => {
    const response = await api.delete(`/api/policies/${id}`)
    return response.data
  },

  getStatistics: async () => {
    const response = await api.get('/api/policies/statistics')
    return response.data
  },

  aiEnhance: async (policyData) => {
    const response = await api.post('/api/policies/ai-enhance', policyData)
    return response.data
  }
}

// AI APIs
export const aiApi = {
  suggestIssueRating: async (issueData) => {
    const response = await api.post('/api/ai/suggest-issue-rating', issueData)
    return response.data
  },

  enrichIssue: async (issueData) => {
    const response = await api.post('/api/ai/enrich-issue', issueData)
    return response.data
  }
}

// Control semantic tags API
export const controlSemanticTagsApi = {
  getSemanticTags: async (controlId) => {
    const response = await api.get(`/api/semantic-tags/control/${controlId}`)
    return response.data
  },

  regenerateSemanticTags: async (controlId) => {
    const response = await api.post(`/api/semantic-tags/control/${controlId}/regenerate`)
    return response.data
  }
}

export default api;